import useSWR, {useSWRConfig} from 'swr'

import type {Iri, UrlId} from '../../../@types/Api'
import type {UserCollection, UserCurrent, UserItem, UserPartial} from '../../../@types/entity/User'
import type {FetchOptions} from '../../../@types/fetch'
import {type UserCollectionFilterGroups, userCollectionFilterMapping, type UserCollectionSort} from '../../../@types/filter/User'
import type {CollectionMap} from '../../../@types/hydra/HydraCollection'
import type {HydraCollectionMember} from '../../../@types/hydra/HydraCollectionMember'
import {useStableParamValue} from '../../components/StableRouter'
import {useLogged} from '../../states/auth'
import {createCollectionIri, createItemIri} from '../../utils/iri'
import {type FetchOption, filterToQueryString, type StrictUnion, useMap, usePatch, usePost} from '../useApi'
import {useAuthenticatedFetcher} from '../useAuth'
import {useEvent} from '../useEvent'
import {useFetcher} from '../useFetcher'
import {useMutateResource} from '../useMutate'

export type UserMap = CollectionMap<UserPartial>
const resource = createCollectionIri('users')

type GetCurrentResponse = {
  readonly user?: UserCurrent
  readonly error?: Error
}

type GetResponse = {
  readonly user?: UserItem
  readonly error?: Error
}

type CollectionResponse = {
  readonly users?: UserCollection
  readonly error?: Error
}

type MapResponse = {
  readonly userMap?: UserMap
  readonly error?: Error
}

type UserCollectionFilter = StrictUnion<UserCollectionFilterGroups>
export type {UserCollectionSort}

export const useUserGet = (userIri?: Iri): GetResponse => {
  const {data, error} = useSWR(userIri)

  return {
    user: data,
    error,
  }
}

export const useUserHelperGetCurrent = (): GetCurrentResponse => {
  const logged = useLogged()
  const authenticatedFetcher = useAuthenticatedFetcher()
  const {data, error} = useSWR(logged ? createCollectionIri('me') : undefined, authenticatedFetcher)

  if (data) {
    // eslint-disable-next-line react-hooks/immutability
    data.isCurrent = true
  }
  return {
    user: data,
    error,
  }
}

export const useUserHelperGetOrCurrent = (userIri?: Iri | 'me'): GetResponse => {
  const isCurrent = userIri === 'me'
  const {user: currentUser, error: currentUserError} = useUserHelperGetCurrent()

  const {data, error} = useSWR(isCurrent ? undefined : userIri)

  if (isCurrent || (data && currentUser && data?.['@id'] === currentUser?.['@id'])) {
    return {
      user: currentUser,
      error: currentUserError,
    }
  }

  return {
    user: data,
    error,
  }
}

export const useUserCollection = (filter: UserCollectionFilter | undefined, fetchOptions?: FetchOption<UserCollectionSort>, enabled = true): CollectionResponse => {
  const querystring = filterToQueryString(userCollectionFilterMapping, enabled ? filter : undefined, fetchOptions)
  const {data, error} = useSWR(enabled && querystring ? `${createCollectionIri('users')}?${querystring.toString()}` : null)
  return {
    users: data,
    error,
  }
}

export const useUserIriFromLocation = (): Iri | 'me' | undefined => {
  const userId = useStableParamValue('userId') as UrlId | undefined

  if (userId === 'me') {
    return 'me'
  }

  return createItemIri('users', userId)
}

export const useUserCollectionToMap = ({users, error}: CollectionResponse): MapResponse => {
  const userMap = useMap<UserPartial>(users)

  if (error) {
    return {
      error,
    }
  }
  if (!users) {
    return {}
  }

  return {
    userMap,
  }
}

export function useUsersFetcher(): (filter: UserCollectionFilter, init?: FetchOptions) => Promise<UserCollection> {
  const {fetcher} = useFetcher()

  return useEvent((filter: UserCollectionFilter, init: FetchOptions = {}): Promise<UserCollection> => {
    const querystring = filterToQueryString(userCollectionFilterMapping, filter, {itemsPerPage: 5})

    return fetcher(querystring ? `${resource}?${querystring.toString()}` : resource, init)
  })
}

export function useUserAvatarUpdate(): (user: UserPartial, image: Blob) => Promise<UserCurrent> {
  const authenticatedFetcher = useAuthenticatedFetcher()
  const {mutate} = useSWRConfig()

  return async function userAvatarUpdate(user: UserPartial, image: Blob) {
    const data = new FormData()
    data.append('image', image)

    const response = (await authenticatedFetcher(`${user['@id']}/avatar`, {
      method: 'POST',
      body: data,
    })) as UserCurrent
    await mutate(createCollectionIri('me'), response)
    response.isCurrent = true

    return response
  }
}

type CreatePayload = {
  email: string
  spot: Iri
}

type UpdatePayload = {
  name?: string
  routeScale?: null | string
  boulderScale?: null | string
  notifyRoute?: boolean
  notifyComment?: boolean
  suggestRoute?: boolean
  locale?: string
}

export function useUserUpdate(): (user: UserPartial, payload: UpdatePayload) => Promise<UserCurrent> {
  const {mutate} = useSWRConfig()
  const handler = usePatch<UserCurrent>()

  return async function userUpdate(user: UserPartial, payload: UpdatePayload) {
    const response = await handler(user, payload)
    await mutate(createCollectionIri('me'), response)
    response.isCurrent = true

    return response
  }
}

export function useUserCreate(): (payload: CreatePayload) => Promise<UserItem> {
  return usePost<UserItem>('users')
}

export function useUserDelete(): (spot: UserPartial) => Promise<void> {
  const authenticatedFetcher = useAuthenticatedFetcher()
  const {mutate} = useSWRConfig()
  const mutateResource = useMutateResource()

  return useEvent(async (item: HydraCollectionMember) => {
    await authenticatedFetcher(item['@id'], {
      method: 'DELETE',
    })
    setTimeout(async () => {
      await mutateResource(item['@id'])
      await mutate(createCollectionIri('me'))
    }, 10)
  })
}
